import { Analytics } from '@genoa/analytics'
import { PaymentMethodType } from '@genoa/domain'
import { BALANCE_OUT_OF_RANGE, deepReplaceContent } from '@genoa/screen-content'

import { useEmbedAnalytics, useSecureLineOfCredit } from '../../../../../../hooks'
import { useCustomerWalletState } from '../../../../../../modules/flex2/use-state'
import { useContent } from '../../../../../../providers/content'
import { Gap, Headline1, PaymentScheduleItemProps, PrimaryButton, SmallText } from '../../../../../components'
import { BasePageLayout } from '../../../../../layouts'
import { PaymentSchedule } from './PaymentSchedule'
import { PaymentScheduleList, PaymentScheduleTable } from './PaymentScheduleTable'

type PaymentScheduleProps = {
  isSlc: boolean
  membershipFee: number
  onMembershipInfoPress?: () => void
  onPressConfirm: () => void
  payments: PaymentScheduleItemProps[]
  paymentStatusSchedule?: PaymentScheduleItemProps[]
  processingFeePercentage?: number
  propertyPassthroughFee?: number
  rentAmount: string
  scheduleList: PaymentScheduleList
  showProcessingFee: boolean
}

export const BalanceOutOfRangePaymentSchedule = (props: PaymentScheduleProps) => {
  const { embedProperties } = useEmbedAnalytics()
  const { isEnabledForSLC, isSimplifiedPaymentsEnabled, isPricingEnabled } = useSecureLineOfCredit()
  const {
    content: { ACCEPTANCE_OFFER_DETAILS_OVERWRITE },
    replaceContent,
  } = useContent()

  // Bill payment experiment replacements
  const feesDisclaimer = replaceContent(ACCEPTANCE_OFFER_DETAILS_OVERWRITE.FEE_DISCLAIMER, {
    processingFeePercentage: props.processingFeePercentage,
  })
  const creditFeesDisclaimer = replaceContent(ACCEPTANCE_OFFER_DETAILS_OVERWRITE.FEE_DISCLAIMER_CREDIT || '', {
    processingFeePercentage: props.processingFeePercentage,
  })

  // Bill payment experiment setup
  const customerWalletState = useCustomerWalletState()
  const defaultCard = customerWalletState?.defaultCard
  const feeDisclaimerLabel = defaultCard?.card_type === PaymentMethodType.CREDIT ? creditFeesDisclaimer : feesDisclaimer

  const content = deepReplaceContent(BALANCE_OUT_OF_RANGE.BALANCE_TOO_HIGH.PAYMENT_SCHEDULE, {
    amount: props.rentAmount,
  })

  return (
    <BasePageLayout
      analyticsScreenName={Analytics.Screens.EMBED_BALANCE_OUT_OF_RANGE_SCHEDULE}
      analyticsScreenParams={embedProperties}
    >
      <Gap size="spacing_200" />
      <Headline1>{content.HEADER}</Headline1>
      <Gap size="spacing_100" />
      <SmallText>{content.SUBTEXT}</SmallText>
      <Gap size="spacing_200" />
      {props.isSlc && <PaymentSchedule payments={props.payments} title="Each month" />}
      {!!props.paymentStatusSchedule && !props.isSlc && (
        <PaymentSchedule payments={props.paymentStatusSchedule} title="Each month" />
      )}
      {!props.paymentStatusSchedule && !props.isSlc && (
        <PaymentScheduleTable
          testID="PaymentSchedule"
          onMembershipLearnMore={props.onMembershipInfoPress}
          fee={props.membershipFee || 0}
          schedule={props.scheduleList}
          processingFeePercentage={props.processingFeePercentage}
          analyticsEvent={Analytics.Events.OUT_OF_RANGE_PAYMENT_SCHEDULE_RENT_INFO_CLICKED}
          tableTitle="Each Month"
          showProcessingFee={props.showProcessingFee}
          feeDisclaimerLabel={feeDisclaimerLabel}
          isEnabledForSLC={isEnabledForSLC}
          isSimplifiedPaymentsEnabled={isSimplifiedPaymentsEnabled}
          showInterestFee={isPricingEnabled}
          propertyPassthroughFee={props.propertyPassthroughFee}
        />
      )}
      <Gap size="spacing_100" />
      <PrimaryButton onClick={props.onPressConfirm}>{content.CTA}</PrimaryButton>
    </BasePageLayout>
  )
}
